<template>
  <div>
    <v-card variant="flat" class="mx-auto my-4 app-content-card">
      <v-card-title>
        {{ $t("candidateDetails.comments.title") }}
      </v-card-title>
      <v-card-text>
        <v-divider></v-divider>
        <br>
        <div class="mb-4 d-flex justify-start">
          <v-btn color="primary" @click="openAddCommentDialog">
            {{ $t("candidateDetails.comments.add") }}
          </v-btn>
        </div>
        <div v-if="items.length" class="cd-comments">
          <div v-for="comment in items" :key="comment.id" class="cd-comment">
            <div class="cd-comment__main">
              <p class="cd-comment__text">{{ comment.Comment }}</p>
              <div class="cd-comment__meta">
                <v-icon size="12" class="cd-comment__meta-icon">mdi-account-outline</v-icon>
                <span>{{ comment.Added_by || comment["Added by"] || comment.AddedBy || $t("candidateDetails.comments.unknown") }}</span>
                <v-icon size="12" class="cd-comment__meta-icon ml-2">mdi-clock-outline</v-icon>
                <span>{{ formatDate(comment.Added_at || comment["Added at"]) }}</span>
              </div>
            </div>
            <div class="cd-comment__actions">
              <v-btn icon variant="text" size="x-small" :title="$t('app.actions.edit')" @click="openEditCommentDialog(comment)">
                <v-icon size="16" color="primary">mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon variant="text" size="x-small" :title="$t('app.actions.delete')" @click="confirmDeleteComment(comment.id)">
                <v-icon size="16" color="error">mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="addCommentDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t("candidateDetails.comments.addTitle") }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="addComment">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-textarea :label="$t('candidateDetails.comments.field')" v-model="newComment.Comment" :rules="[rules.required]" outlined required></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-btn color="primary" type="submit">{{ $t("app.actions.add") }}</v-btn>
                  <v-btn variant="text" @click="closeAddCommentDialog">{{ $t("app.actions.cancel") }}</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editCommentDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t("candidateDetails.comments.editTitle") }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="updateComment">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-textarea :label="$t('candidateDetails.comments.field')" v-model="editComment.Comment" :rules="[rules.required]" outlined required></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-btn color="primary" type="submit">{{ $t("app.actions.save") }}</v-btn>
                  <v-btn variant="text" @click="closeEditCommentDialog">{{ $t("app.actions.cancel") }}</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";

// Comments timeline + CRUD tab extracted verbatim from CandidateDetails.vue.
// `currentUser` (for the Added_by stamp) is supplied by the parent; the parent
// reloads comments on `refresh` (its dedicated fetchComments).
export default defineComponent({
  name: "CandidateCommentsTab",
  props: {
    candidateId: { type: [String, Number], required: true },
    items: { type: Array, default: () => [] },
    currentUser: { type: Object, default: () => ({}) },
  },
  emits: ["refresh", "notify"],
  data() {
    return {
      addCommentDialog: false,
      newComment: { Comment: "" },
      editCommentDialog: false,
      editComment: { id: null, Comment: "" },
    };
  },
  computed: {
    rules() {
      return { required: (value) => !!value || this.$t("candidateTabs.common.requiredField") };
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return "";
      const datePart = String(dateString).split("T")[0];
      const m = datePart.match(/^(\d{4})-(\d{2})-(\d{2})/);
      return m ? `${m[3]}.${m[2]}.${m[1]}` : datePart;
    },
    openAddCommentDialog() {
      this.newComment = { Comment: "" };
      this.addCommentDialog = true;
    },
    closeAddCommentDialog() {
      this.addCommentDialog = false;
    },
    addComment() {
      if (!this.newComment.Comment || !this.newComment.Comment.trim()) {
        this.$emit("notify", { message: this.$t("candidateDetails.comments.requiredEmpty"), color: "error" });
        return;
      }
      const addedBy = this.currentUser && this.currentUser.name ? this.currentUser.name : "Unknown";
      const payload = {
        id_foreign: this.candidateId,
        Comment: this.newComment.Comment,
        Added_by: addedBy,
        "Added by": addedBy,
      };
      axios
        .post("/candidati/comments", payload)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateDetails.comments.added"), color: "success" });
          this.addCommentDialog = false;
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la adăugarea comentariului:", error);
          this.$emit("notify", { message: this.$t("candidateDetails.comments.addError"), color: "error" });
        });
    },
    openEditCommentDialog(comment) {
      this.editComment = { ...comment };
      this.editCommentDialog = true;
    },
    closeEditCommentDialog() {
      this.editCommentDialog = false;
    },
    updateComment() {
      if (!this.editComment.Comment.trim()) {
        this.$emit("notify", { message: this.$t("candidateDetails.comments.requiredEmpty"), color: "error" });
        return;
      }
      const commentId = this.editComment.id;
      const payload = { id_foreign: this.candidateId, Comment: this.editComment.Comment };
      axios
        .put(`/candidati/comments/${commentId}`, payload)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateDetails.comments.updated"), color: "success" });
          this.editCommentDialog = false;
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la actualizarea comentariului:", error);
          this.$emit("notify", { message: this.$t("candidateDetails.comments.updateError"), color: "error" });
        });
    },
    confirmDeleteComment(id) {
      if (confirm(this.$t("candidateDetails.comments.deleteConfirm"))) {
        this.deleteComment(id);
      }
    },
    deleteComment(id) {
      axios
        .delete(`/candidati/comments/${id}`)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateDetails.comments.deleted"), color: "success" });
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la ștergerea comentariului:", error);
          this.$emit("notify", { message: this.$t("candidateDetails.comments.deleteError"), color: "error" });
        });
    },
  },
});
</script>

<style scoped>
/* Listă densă de comentarii (fără timeline cu buline verzi din afara paletei). */
.cd-comments {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.cd-comment {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  border: 1px solid var(--ncrm-color-border-subtle, rgba(var(--v-theme-on-surface), 0.06));
  border-left: 3px solid rgba(var(--v-theme-primary), 0.5);
  border-radius: var(--app-radius-base);
}
.cd-comment__main {
  min-width: 0;
  flex: 1 1 auto;
}
.cd-comment__text {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.35;
  color: var(--app-text-strong);
  white-space: pre-line;
  word-break: break-word;
}
.cd-comment__meta {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 3px;
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.5);
}
.cd-comment__meta-icon {
  opacity: 0.6;
  flex-shrink: 0;
}
.cd-comment__actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}
</style>
